import React, { useState } from 'react';
import SiteNav from '../components/SiteNav';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';

const mono: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace" };

const CONTACT_EMAIL = 'desk@btq.com';
const CONTACT_MAILTO = 'mailto:desk@btq.com?subject=QSSN product inquiry';

// Formspree endpoint — create a form at https://formspree.io, then paste its URL here
// (looks like https://formspree.io/f/abcdwxyz). Point it at desk@btq.com in the Formspree dashboard.
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mkolqpdg';

const ArrowRight = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" style={{ stroke: 'currentColor', strokeWidth: 1.6 }}>
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

const fieldStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--bg)',
  border: '1px solid var(--line)',
  borderRadius: 9,
  padding: '.8rem 1rem',
  font: 'inherit',
  fontSize: '.9375rem',
  color: 'var(--ink)',
  outline: 'none',
};

const labelText: React.CSSProperties = { display: 'block', fontSize: '.8125rem', color: 'var(--ink-2)', marginBottom: '.45rem' };

const STEPS = [
  { n: '01', title: 'PQC evaluation and working session', body: 'We map your privileged actions and where a quantum-safe approval fits.' },
  { n: '02', title: 'Contained pilot plan', body: 'Named roles, rollback criteria, and the evidence to carry forward.' },
];

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label style={{ display: 'block' }}>
    <span style={labelText}>{label}</span>
    {children}
  </label>
);

const Contact: React.FC = () => {
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setStatus('sending');
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        form.reset();
        setStatus('sent');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };
  const onFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.target.style.borderColor = 'var(--accent)';
    e.target.style.boxShadow = '0 0 0 3px var(--accent-soft)';
  };
  const onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.target.style.borderColor = 'var(--line)';
    e.target.style.boxShadow = 'none';
  };

  return (
    <>
      <SiteNav />

      <section style={{ background: 'var(--bg)', padding: '4.25rem 1.5rem 7rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Reveal
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '3rem',
            }}
          >
            <span style={{ ...mono, fontSize: '.6875rem', letterSpacing: '.18em', textTransform: 'uppercase', whiteSpace: 'nowrap', color: 'var(--ink-3)' }}>{'Contact'}</span>
          </Reveal>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3.5rem 5rem', alignItems: 'flex-start' }}>
            {/* left */}
            <div style={{ flex: '1 1 400px' }}>
              <Reveal
                as="h1"
                style={{
                  margin: 0,
                  fontWeight: 500,
                  fontSize: 'clamp(2.4rem,4.4vw,3.7rem)',
                  lineHeight: 1.02,
                  letterSpacing: '-.025em',
                  color: 'var(--ink)',
                  textWrap: 'balance',
                } as React.CSSProperties}
              >
                Let's talk about quantum-safe control infrastructure.
              </Reveal>
              <Reveal as="p" delay={80} style={{ margin: '1.75rem 0 0', maxWidth: '34rem', fontSize: '1.125rem', lineHeight: 1.6, color: 'var(--ink-2)' }}>
                For banks, issuers, and regulated digital-asset teams evaluating post-quantum authorization around existing asset operations.
              </Reveal>

              <Reveal style={{ marginTop: '2.5rem' }}>
                {STEPS.map((s) => (
                  <div key={s.n} style={{ display: 'grid', gridTemplateColumns: '2.5rem 1fr', gap: '1.25rem', padding: '1.4rem 0', borderTop: '1px solid var(--line)' }}>
                    <span style={{ ...mono, fontSize: '.75rem', color: 'var(--accent)' }}>{s.n}</span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 500, color: 'var(--ink)' }}>{s.title}</h3>
                      <p style={{ margin: '.4rem 0 0', lineHeight: 1.55, color: 'var(--ink-2)' }}>{s.body}</p>
                    </div>
                  </div>
                ))}
              </Reveal>

              <Reveal style={{ marginTop: '2rem' }}>
                <div style={{ ...mono, fontSize: '.625rem', letterSpacing: '.16em', textTransform: 'uppercase', whiteSpace: 'nowrap', color: 'var(--ink-3)' }}>Or email us directly</div>
                <a href={CONTACT_MAILTO} style={{ display: 'inline-block', marginTop: '.5rem', fontSize: '1.125rem', fontWeight: 500, color: 'var(--accent)', textDecoration: 'none' }}>
                  {CONTACT_EMAIL}
                </a>
              </Reveal>
            </div>

            {/* right: form */}
            <Reveal delay={120} style={{ flex: '1 1 420px', maxWidth: 520 }}>
              <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 16, boxShadow: '0 1px 2px rgba(16,18,24,.04),0 30px 60px -34px rgba(16,18,24,.2)', overflow: 'hidden' }}>
                {status !== 'sent' ? (
                  <form onSubmit={onSubmit} style={{ padding: '1.75rem' }}>
                    <div style={{ ...mono, fontSize: '.625rem', letterSpacing: '.16em', textTransform: 'uppercase', whiteSpace: 'nowrap', color: 'var(--ink-3)', marginBottom: '1.4rem' }}>
                      Start a conversation
                    </div>
                    <input type="hidden" name="_subject" value="QSSN product inquiry" />
                    <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ display: 'none' }} />
                    <div style={{ display: 'grid', gap: '1.1rem' }}>
                      <Field label="Full name">
                        <input type="text" name="name" required placeholder="Jordan Rivera" style={fieldStyle} onFocus={onFocus} onBlur={onBlur} />
                      </Field>
                      <Field label="Work email">
                        <input type="email" name="email" required placeholder="jordan@institution.com" style={fieldStyle} onFocus={onFocus} onBlur={onBlur} />
                      </Field>
                      <Field label="Organization">
                        <input type="text" name="org" placeholder="Bank, issuer, or fund" style={fieldStyle} onFocus={onFocus} onBlur={onBlur} />
                      </Field>
                      <Field label="What are you evaluating?">
                        <textarea name="message" rows={4} placeholder="A few words on your asset program and what you'd like to harden." style={{ ...fieldStyle, resize: 'vertical' }} onFocus={onFocus} onBlur={onBlur} />
                      </Field>
                    </div>
                    <button
                      type="submit"
                      disabled={status === 'sending'}
                      style={{
                        marginTop: '1.5rem',
                        width: '100%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '.55rem',
                        background: 'var(--accent)',
                        color: '#fff',
                        border: 'none',
                        cursor: status === 'sending' ? 'default' : 'pointer',
                        opacity: status === 'sending' ? 0.7 : 1,
                        font: 'inherit',
                        fontSize: '.9375rem',
                        fontWeight: 500,
                        padding: '.95rem 1.4rem',
                        borderRadius: 9,
                      }}
                    >
                      {status === 'sending' ? 'Sending…' : 'Send inquiry'}
                      {status !== 'sending' && <ArrowRight />}
                    </button>
                    {status === 'error' ? (
                      <p style={{ margin: '1rem 0 0', fontSize: '.75rem', lineHeight: 1.5, color: '#9F3326' }}>
                        Something went wrong sending your message. Please email {CONTACT_EMAIL} directly.
                      </p>
                    ) : (
                      <p style={{ margin: '1rem 0 0', fontSize: '.75rem', lineHeight: 1.5, color: 'var(--ink-3)' }}>
                        We reply from a named contact, usually within two business days.
                      </p>
                    )}
                  </form>
                ) : (
                  <div style={{ padding: '3rem 1.75rem', textAlign: 'center' }}>
                    <div style={{ margin: '0 auto', width: 48, height: 48, borderRadius: '50%', background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)' }}>
                      <svg viewBox="0 0 24 24" width="24" height="24" fill="none" style={{ stroke: 'currentColor', strokeWidth: 1.8 }}>
                        <path d="M5 12.5 10 17.5 19 7" />
                      </svg>
                    </div>
                    <h3 style={{ margin: '1.25rem 0 0', fontSize: '1.5rem', fontWeight: 500, color: 'var(--ink)', letterSpacing: '-.01em' }}>Your inquiry is in.</h3>
                    <p style={{ margin: '.75rem auto 0', maxWidth: '24rem', lineHeight: 1.6, color: 'var(--ink-2)' }}>
                      A QSSN team member will review your note and reply from a named address within two business days. For urgent requests, email {CONTACT_EMAIL}.
                    </p>
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Footer cta={false} />
    </>
  );
};

export default Contact;
